import { getOptimizationFailureExplanationKey } from 'helpers/optimize.helper/optimize.helper'
import i18n from 'i18next'
import React, { ReactElement } from 'react'
import { NavigateFunction } from 'react-router'
import { FILTER_QUERYSTRING_PARAM, GROUPING_QUERYSTRING_PARAM } from 'store/filter/urlFilter.helper'
import StatusIcon from 'ui/atoms/StatusIcon/StatusIcon'
import Datetime from 'utils/datetime/datetime'
import styles from './NotificationCenter.module.less'

export type SubtypeObject = {
  name: string
  display_name: string
}

export type NotificationType = {
  system_id: number
  id: number
  type:
    | 'optimization'
    | 'setting'
    | 'admin'
    | 'OperationalEventActivated'
    | 'OperationalEventSent'
    | 'OperationalEventDeactivated'
    | 'OperationalEventDeleted'
  priority: number
  creator_name: string
  route?: string
  created_at: ISODateTime
  updated_by: string
  description: string
  updated_at: ISODateTime
  meta?: {
    opt_job_type?:
      | 'regular'
      | 'regular_wip'
      | 'sandbox'
      | 'measvalues_calculations'
      | 'followup_without_deviations'
      | 'followup_with_deviations'
      | 'fuel_plan'
    job_id?: number
    setting_attribute?: 'availability' | 'forced'
    setting_type?: 'deviation' | 'base'
    comment?: string
    start_time?: ISODateTime
    end_time?: ISODateTime
    status?:
      | 'finished'
      | 'failed'
      | 'running'
      | 'queued'
      | 'preparing'
      | 'unsolvable'
      | 'created'
      | 'processing'
      | 'solved' //Created → Preparing → Queued → Running → Solved → Processing → Finished
    subtypes?: SubtypeObject[]
    event_name?: string
    event_id?: number
  }
}

export const optimizationFilters = ['finished', 'failed', 'manual', 'automatic']
export const eventFilters = ['sent', 'active', 'inactive', 'deleted']
export const optimizationFilterLabels: Record<string, string> = {
  finished: i18n.t('Finished'),
  failed: i18n.t('Failed'),
  manual: i18n.t('Manual'),
  automatic: i18n.t('Automatic'),
}

export const eventFilterLabels: Record<string, string> = {
  sent: i18n.t('Sent'),
  active: i18n.t('Active'),
  inactive: i18n.t('Inactive'),
  deleted: i18n.t('Deleted'),
}

export const ACTIVE_STATUSES = ['created', 'queued', 'running']
const TERMINAL_STATUSES = ['finished', 'failed', 'unsolvable']
/**
 * An optimization that came back `unsolvable` did not fail on its way to a result — it reached
 * one, and the result is that no feasible plan exists. To everyone but the solver that is a
 * failure: there is nothing to look at and the input has to change.
 */
const FAILED_STATUSES = ['failed', 'unsolvable']

type OptimizationNotificationStatus = NonNullable<NotificationType['meta']>['status']

export function isFailedOptimizationStatus(status: OptimizationNotificationStatus): boolean {
  return status !== undefined && FAILED_STATUSES.includes(status)
}

export function isTerminalOptimizationStatus(status: OptimizationNotificationStatus): boolean {
  return status !== undefined && TERMINAL_STATUSES.includes(status)
}

/**
 * Bridges this module's lowercase status vocabulary to the shared explanations, so a failure
 * reads the same here as it does in the opt job log. Returns a translation key; `StatusIcon`
 * resolves it.
 */
function failureExplanationKey(status: OptimizationNotificationStatus): string | undefined {
  if (status === 'unsolvable') return getOptimizationFailureExplanationKey('Unsolvable')
  if (status === 'failed') return getOptimizationFailureExplanationKey('Failed')

  return undefined
}

export function getTime(time: ISODateTime, label?: string, noPadding?: boolean): ReactElement {
  let newTime: string = time
  const today = Datetime.getTodayDate()
  if (Datetime.isSameDate(time, today.startTime)) {
    newTime = Datetime.toLocalTime(time, 'hour')
  } else if (Datetime.isAfter(time, Datetime.getYearToDate().startTime)) {
    newTime = Datetime.toLocalTime(time, 'longDayTextWithoutYear')
  } else {
    newTime = Datetime.toLocalTime(time, 'longDayText')
  }

  return (
    <div className={noPadding ? undefined : styles.NotificationCenter_NotificationItemContainer_Time}>
      {label} {newTime}
    </div>
  )
}

export function onLinkClicked(route: string, navigate: NavigateFunction): void {
  const currentParams = new URLSearchParams(window.location.search)

  // Extract pathname, search and hash from the route string robustly
  const origin = window.location.origin
  const parsed = new URL(route, origin) // handles relative routes too
  const basePathWithoutSearch = parsed.pathname
  const paramsFromRoute = new URLSearchParams(parsed.search) // now contains startTime/endTime if present
  const targetParams = new URLSearchParams(paramsFromRoute.toString()) // clone

  // Only preserve 'filter' and 'group' if not already present in the route
  const filterVal = currentParams.get(FILTER_QUERYSTRING_PARAM)
  if (filterVal && !paramsFromRoute.has(FILTER_QUERYSTRING_PARAM)) {
    targetParams.set(FILTER_QUERYSTRING_PARAM, filterVal)
  }
  const groupVal = currentParams.get(GROUPING_QUERYSTRING_PARAM)
  if (groupVal && !paramsFromRoute.has(GROUPING_QUERYSTRING_PARAM)) {
    targetParams.set(GROUPING_QUERYSTRING_PARAM, groupVal)
  }

  // navigate, preserving hash from the route if any
  const queryString = targetParams.toString()
  const hash = parsed.hash || ''
  navigate({ pathname: basePathWithoutSearch, search: queryString, hash }, { replace: true })
}

function getRegularWipOptimizationLog(
  item: NotificationType,
  onLinkClicked: (route: string) => void
): ReactElement | undefined {
  let statusIcon = (
    <StatusIcon
      variant="spinner"
      tooltip={{
        title: i18n.t('Loading...'),
      }}
    />
  )
  let optimizationDescription = ''
  const createdBy = `${i18n.t('Created by')} ${item.creator_name}`
  const status = item.meta?.status
  const hasSubtype = item.meta?.subtypes
  const subtype = hasSubtype ? hasSubtype.map((subtype: SubtypeObject) => subtype.display_name).join(', ') : ''
  const typeOfOptimization = i18n.t('draft production plan optimization')
  if (status === 'running') {
    if (hasSubtype) {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} for {{subtype}} has been created.', {
        typeOfOptimization: typeOfOptimization,
        subtype: subtype,
      })
    } else {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} has been created.', {
        typeOfOptimization: typeOfOptimization,
      })
    }
  } else if (status === 'queued') {
    statusIcon = (
      <StatusIcon
        variant="queued"
        tooltip={{
          title: i18n.t('Queued'),
        }}
      />
    )
    if (hasSubtype) {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} for {{subtype}} has been queued.', {
        typeOfOptimization: typeOfOptimization,
        subtype: subtype,
      })
    } else {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} has been queued.', {
        typeOfOptimization: typeOfOptimization,
      })
    }
  } else if (status === 'created') {
    if (hasSubtype) {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} for {{subtype}} has been created.', {
        typeOfOptimization: typeOfOptimization,
        subtype: subtype,
      })
    } else {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} has been created.', {
        typeOfOptimization: typeOfOptimization,
      })
    }
  } else if (status === 'unsolvable') {
    statusIcon = (
      <StatusIcon
        variant="regular_unsolvable"
        tooltip={{
          title: failureExplanationKey(status),
        }}
      />
    )
    if (hasSubtype) {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} for {{subtype}} is unsolvable.', {
        typeOfOptimization: typeOfOptimization,
        subtype: subtype,
      })
    } else {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} is unsolvable.', {
        typeOfOptimization: typeOfOptimization,
      })
    }
  } else if (status === 'failed') {
    statusIcon = (
      <StatusIcon
        variant="regular_failed"
        tooltip={{
          title: failureExplanationKey(status),
        }}
      />
    )
    if (hasSubtype) {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} for {{subtype}} has failed.', {
        typeOfOptimization: typeOfOptimization,
        subtype: subtype,
      })
    } else {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} has failed.', {
        typeOfOptimization: typeOfOptimization,
      })
    }
  } else if (status === 'finished') {
    statusIcon = (
      <StatusIcon
        variant="regular_finished"
        tooltip={{
          title: i18n.t('Finished'),
        }}
      />
    )
    if (hasSubtype) {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} for {{subtype}} has been completed.', {
        typeOfOptimization: typeOfOptimization,
        subtype: subtype,
      })
    } else {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} has been completed.', {
        typeOfOptimization: typeOfOptimization,
      })
    }
  }
  return (
    <div className={styles.NotificationCenter_NotificationItemContainer}>
      <div className={styles.NotificationCenter_NotificationItemContainer_Icon}>{statusIcon}</div>
      <div className={styles.NotificationCenter_NotificationItemContainer_Description}>{optimizationDescription}</div>
      {item.creator_name && (
        <div className={styles.NotificationCenter_NotificationItemContainer_Description__italic}>{createdBy}</div>
      )}
      {!item.creator_name && (
        <div className={styles.NotificationCenter_NotificationItemContainer_Description__italic}>
          {i18n.t('Automatic optimization')}
        </div>
      )}
      {isTerminalOptimizationStatus(status) ? (
        <a
          className={styles.NotificationCenter_NotificationItemContainer_Link}
          onClick={() => onLinkClicked('/draftproductionplan')}
        >
          {i18n.t('Go to the draft production plan')}
        </a>
      ) : (
        <></>
      )}
    </div>
  )
}

function getRegularOptimizationLog(
  item: NotificationType,
  onLinkClicked: (route: string) => void
): ReactElement | undefined {
  let statusIcon = (
    <StatusIcon
      variant="spinner"
      tooltip={{
        title: i18n.t('Loading...'),
      }}
    />
  )
  let optimizationDescription = ''
  const createdBy = `${i18n.t('Created by')} ${item.creator_name}`
  const status = item.meta?.status
  const hasSubtype = item.meta?.subtypes
  const subtype = hasSubtype ? hasSubtype.map((subtype: SubtypeObject) => subtype.display_name).join(', ') : ''
  const typeOfOptimization = i18n.t('production plan optimization')
  if (status === 'running') {
    if (hasSubtype) {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} for {{subtype}} has been created.', {
        typeOfOptimization: typeOfOptimization,
        subtype: subtype,
      })
    } else {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} has been created.', {
        typeOfOptimization: typeOfOptimization,
      })
    }
  } else if (status === 'queued') {
    statusIcon = (
      <StatusIcon
        variant="queued"
        tooltip={{
          title: i18n.t('Queued'),
        }}
      />
    )
    if (hasSubtype) {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} for {{subtype}} has been queued.', {
        typeOfOptimization: typeOfOptimization,
        subtype: subtype,
      })
    } else {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} has been queued.', {
        typeOfOptimization: typeOfOptimization,
      })
    }
  } else if (status === 'created') {
    if (hasSubtype) {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} for {{subtype}} has been created.', {
        typeOfOptimization: typeOfOptimization,
        subtype: subtype,
      })
    } else {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} has been created.', {
        typeOfOptimization: typeOfOptimization,
      })
    }
  } else if (status === 'unsolvable') {
    statusIcon = (
      <StatusIcon
        variant="regular_unsolvable"
        tooltip={{
          title: failureExplanationKey(status),
        }}
      />
    )
    if (hasSubtype) {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} for {{subtype}} is unsolvable.', {
        typeOfOptimization: typeOfOptimization,
        subtype: subtype,
      })
    } else {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} is unsolvable.', {
        typeOfOptimization: typeOfOptimization,
      })
    }
  } else if (status === 'failed') {
    statusIcon = (
      <StatusIcon
        variant="regular_failed"
        tooltip={{
          title: failureExplanationKey(status),
        }}
      />
    )
    if (hasSubtype) {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} for {{subtype}} has failed.', {
        typeOfOptimization: typeOfOptimization,
        subtype: subtype,
      })
    } else {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} has failed.', {
        typeOfOptimization: typeOfOptimization,
      })
    }
  } else if (status === 'finished') {
    statusIcon = (
      <StatusIcon
        variant="regular_finished"
        tooltip={{
          title: i18n.t('Finished'),
        }}
      />
    )
    if (hasSubtype) {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} for {{subtype}} has been completed.', {
        typeOfOptimization: typeOfOptimization,
        subtype: subtype,
      })
    } else {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} has been completed.', {
        typeOfOptimization: typeOfOptimization,
      })
    }
  }

  return (
    <div className={styles.NotificationCenter_NotificationItemContainer}>
      <div className={styles.NotificationCenter_NotificationItemContainer_Icon}>{statusIcon}</div>
      <div className={styles.NotificationCenter_NotificationItemContainer_Description}>{optimizationDescription}</div>
      {item.creator_name && (
        <div className={styles.NotificationCenter_NotificationItemContainer_Description__italic}>{createdBy}</div>
      )}
      {!item.creator_name && (
        <div className={styles.NotificationCenter_NotificationItemContainer_Description__italic}>
          {i18n.t('Automatic optimization')}
        </div>
      )}
      {isTerminalOptimizationStatus(status) ? (
        <a
          className={styles.NotificationCenter_NotificationItemContainer_Link}
          onClick={() => onLinkClicked('/optimize')}
        >
          {i18n.t('Go to the production plan')}
        </a>
      ) : (
        <></>
      )}
    </div>
  )
}

function getPublishedOptimizationLog(
  item: NotificationType,
  onLinkClicked: (route: string) => void
): ReactElement | undefined {
  let statusIcon = (
    <StatusIcon
      variant="spinner"
      tooltip={{
        title: i18n.t('Loading...'),
      }}
    />
  )
  let optimizationDescription = ''
  const publishedBy = `${i18n.t('Published by')} ${item.creator_name}`
  const status = item.meta?.status
  const hasSubtype = item.meta?.subtypes
  const subtype = hasSubtype ? hasSubtype.map((subtype: SubtypeObject) => subtype.display_name).join(', ') : ''
  const typeOfOptimization = i18n.t('production plan optimization')
  if (status === 'running' || status === 'created') {
    statusIcon = (
      <StatusIcon
        variant="spinner"
        tooltip={{
          title: i18n.t('Loading...'),
        }}
      />
    )

    if (hasSubtype) {
      optimizationDescription = i18n.t('A production plan for {{subtype}} is now being published.', {
        subtype: subtype,
      })
    } else {
      optimizationDescription = i18n.t('A production plan is now being published.')
    }
  } else if (status === 'queued') {
    statusIcon = (
      <StatusIcon
        variant="queued"
        tooltip={{
          title: i18n.t('Queued'),
        }}
      />
    )

    if (hasSubtype) {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} for {{subtype}} has been queued.', {
        typeOfOptimization: typeOfOptimization,
        subtype: subtype,
      })
    } else {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} has been queued.', { typeOfOptimization })
    }
  } else if (status === 'unsolvable' || status === 'failed') {
    statusIcon = (
      <StatusIcon
        variant="regular_wip_failed"
        tooltip={{
          title: failureExplanationKey(status),
        }}
      />
    )

    if (hasSubtype) {
      optimizationDescription = i18n.t('A production plan for {{subtype}} failed to be published.', {
        subtype: subtype,
      })
    } else {
      optimizationDescription = i18n.t('A production plan failed to be published.')
    }
  } else if (status === 'finished') {
    statusIcon = (
      <StatusIcon
        variant="regular_wip_published"
        tooltip={{
          title: i18n.t('Finished'),
        }}
      />
    )
    if (hasSubtype) {
      optimizationDescription = i18n.t('A production plan for {{subtype}} has been published.', {
        subtype: subtype,
      })
    } else {
      optimizationDescription = i18n.t('A production plan has been published.')
    }
  }

  return (
    <div className={styles.NotificationCenter_NotificationItemContainer}>
      <div className={styles.NotificationCenter_NotificationItemContainer_Icon__noBottomPadding}>{statusIcon}</div>
      <div className={styles.NotificationCenter_NotificationItemContainer_Description}>{optimizationDescription}</div>
      {item.creator_name && (
        <div className={styles.NotificationCenter_NotificationItemContainer_Description__italic}>{publishedBy}</div>
      )}
      {isTerminalOptimizationStatus(status) ? (
        <a
          className={styles.NotificationCenter_NotificationItemContainer_Link}
          onClick={() => onLinkClicked('/optimize')}
        >
          {i18n.t('Go to the production plan')}
        </a>
      ) : (
        <></>
      )}
    </div>
  )
}

function getFollowupOptimizationLog(
  item: NotificationType,
  onLinkClicked: (route: string) => void
): ReactElement | undefined {
  let statusIcon = (
    <StatusIcon
      variant="followup_created"
      tooltip={{
        title: i18n.t('Loading...'),
      }}
    />
  )
  let optimizationDescription = ''
  const createdBy = `${i18n.t('Created by')} ${item.creator_name}`
  const status = item.meta?.status
  const hasSubtype = item.meta?.subtypes
  const subtype = hasSubtype ? hasSubtype.map((subtype: SubtypeObject) => subtype.display_name).join(', ') : ''
  const startTime = item.meta?.start_time ? Datetime.toLocalTime(item.meta.start_time) : undefined
  const endTime = item.meta?.end_time
    ? Datetime.toLocalTime(Datetime.ISODatetimeTo59Minutes(item.meta.end_time))
    : undefined

  const period = startTime && endTime ? i18n.t(`{{startTime}} to {{endTime}}`, { startTime, endTime }) : ''
  const followUpType =
    item.meta?.opt_job_type === 'followup_with_deviations'
      ? i18n.t('(incl. production deviations)')
      : i18n.t('(optimal production)')
  if (status === 'running') {
    statusIcon = (
      <StatusIcon
        variant="spinner"
        tooltip={{
          title: i18n.t('Loading...'),
        }}
      />
    )

    if (hasSubtype) {
      optimizationDescription = i18n.t(
        'A follow-up optimization {{followUpType}} for {{subtype}} with the period {{followUpPeriod}} has been created and is now running.',
        { followUpType: followUpType, followUpPeriod: period, subtype: subtype }
      )
    } else {
      optimizationDescription = i18n.t(
        'A follow-up optimization {{followUpType}} with the period {{followUpPeriod}} has been created and is now running.',
        { followUpType: followUpType, followUpPeriod: period }
      )
    }
  } else if (status === 'queued') {
    statusIcon = (
      <StatusIcon
        variant="queued"
        tooltip={{
          title: i18n.t('Queued'),
        }}
      />
    )
    if (hasSubtype) {
      optimizationDescription = i18n.t(
        'A follow-up optimization {{followUpType}} for {{subtype}} with the period {{followUpPeriod}} has been queued.',
        { followUpType: followUpType, subtype: subtype, followUpPeriod: period }
      )
    } else {
      optimizationDescription = i18n.t(
        'A follow-up optimization {{followUpType}} with the period {{followUpPeriod}} has been queued.',
        { followUpType: followUpType, followUpPeriod: period }
      )
    }
  } else if (status === 'created') {
    statusIcon = (
      <StatusIcon
        variant="spinner"
        tooltip={{
          title: i18n.t('Loading...'),
        }}
      />
    )
    if (hasSubtype) {
      optimizationDescription = i18n.t(
        'A follow-up optimization {{followUpType}} for {{subtype}} with the period {{followUpPeriod}} has been created.',
        { followUpType: followUpType, subtype: subtype, followUpPeriod: period }
      )
    } else {
      optimizationDescription = i18n.t(
        'A follow-up optimization {{followUpType}} with the period {{followUpPeriod}} has been created.',
        { followUpType: followUpType, followUpPeriod: period }
      )
    }
  } else if (status === 'unsolvable') {
    if (hasSubtype) {
      optimizationDescription = i18n.t(
        'A follow-up optimization {{followUpType}} for {{subtype}} with the period {{followUpPeriod}} is unsolvable.',
        { followUpType: followUpType, subtype: subtype, followUpPeriod: period }
      )
    } else {
      optimizationDescription = i18n.t(
        'A follow-up optimization {{followUpType}} with the period {{followUpPeriod}} is unsolvable.',
        { followUpType: followUpType, followUpPeriod: period }
      )
    }
    statusIcon = (
      <StatusIcon
        variant="followup_unsolvable"
        tooltip={{
          title: failureExplanationKey(status),
        }}
      />
    )
  } else if (status === 'failed') {
    statusIcon = (
      <StatusIcon
        variant="followup_failed"
        tooltip={{
          title: failureExplanationKey(status),
        }}
      />
    )
    if (hasSubtype) {
      optimizationDescription = i18n.t(
        'A follow-up optimization {{followUpType}} for {{subtype}} with the period {{followUpPeriod}} has failed.',
        { followUpType: followUpType, followUpPeriod: period, subtype: subtype }
      )
    } else {
      optimizationDescription = i18n.t(
        'A follow-up optimization {{followUpType}} with the period {{followUpPeriod}} has failed.',
        { followUpType: followUpType, followUpPeriod: period }
      )
    }
  } else if (status === 'finished') {
    statusIcon = (
      <StatusIcon
        variant="followup_finished"
        tooltip={{
          title: i18n.t('Finished'),
        }}
      />
    )
    if (hasSubtype) {
      optimizationDescription = i18n.t(
        'A follow-up optimization {{followUpType}} for {{subtype}} with the period {{followUpPeriod}} has been completed.',
        { followUpType: followUpType, followUpPeriod: period, subtype: subtype }
      )
    } else {
      optimizationDescription = i18n.t(
        'A follow-up optimization {{followUpType}} with the period {{followUpPeriod}} has been completed.',
        { followUpType: followUpType, followUpPeriod: period }
      )
    }
  }

  let link = ''
  let startTimeDate = ''
  let endTimeDate = ''
  if (item?.meta?.start_time && item.meta.end_time) {
    startTimeDate = item.meta.start_time.split('T')[0]
    endTimeDate = item.meta.end_time.split('T')[0]
    link = `/followupproduction?startTime=${startTimeDate}&endTime=${endTimeDate}`
  }

  return (
    <div className={styles.NotificationCenter_NotificationItemContainer}>
      <div className={styles.NotificationCenter_NotificationItemContainer_Icon}>{statusIcon}</div>
      <div className={styles.NotificationCenter_NotificationItemContainer_Description}>{optimizationDescription}</div>
      {item.creator_name && (
        <div className={styles.NotificationCenter_NotificationItemContainer_Description__italic}>{createdBy}</div>
      )}
      {isTerminalOptimizationStatus(status) ? (
        <a className={styles.NotificationCenter_NotificationItemContainer_Link} onClick={() => onLinkClicked(link)}>
          {i18n.t('Go to the follow-up module')}
        </a>
      ) : (
        <></>
      )}
    </div>
  )
}

function getSandboxOptimizationLog(
  item: NotificationType,
  onLinkClicked: (route: string) => void
): ReactElement | undefined {
  let statusIcon = (
    <StatusIcon
      variant="spinner"
      tooltip={{
        title: i18n.t('Loading...'),
      }}
    />
  )
  let optimizationDescription = ''
  const createdBy = `${i18n.t('Created by')} ${item.creator_name}`
  const status = item.meta?.status
  const typeOfOptimization = i18n.t('sandbox optimization')
  if (status === 'created' || status === 'running') {
    optimizationDescription = i18n.t('A {{typeOfOptimization}} has been created.', { typeOfOptimization })
  } else if (status === 'queued') {
    statusIcon = (
      <StatusIcon
        variant="queued"
        tooltip={{
          title: i18n.t('Queued'),
        }}
      />
    )
    optimizationDescription = i18n.t('A {{typeOfOptimization}} has been queued.', { typeOfOptimization })
  } else if (status === 'failed' || status === 'unsolvable') {
    statusIcon = (
      <StatusIcon
        variant="sandbox_failed"
        tooltip={{
          title: failureExplanationKey(status),
        }}
      />
    )
    optimizationDescription = i18n.t('A {{typeOfOptimization}} has failed.', { typeOfOptimization })
  } else if (status === 'finished') {
    statusIcon = (
      <StatusIcon
        variant="sandbox_finished"
        tooltip={{
          title: i18n.t('Finished'),
        }}
      />
    )
    optimizationDescription = i18n.t('A {{typeOfOptimization}} has finished.', { typeOfOptimization })
  }

  return (
    <div className={styles.NotificationCenter_NotificationItemContainer}>
      <div className={styles.NotificationCenter_NotificationItemContainer_Icon}>{statusIcon}</div>
      <div className={styles.NotificationCenter_NotificationItemContainer_Description}>{optimizationDescription}</div>
      {item.creator_name && (
        <div className={styles.NotificationCenter_NotificationItemContainer_Description__italic}>{createdBy}</div>
      )}
      {
        <a
          className={styles.NotificationCenter_NotificationItemContainer_Link}
          onClick={() => onLinkClicked('/sandbox')}
        >
          {i18n.t('Go to the sandbox module')}
        </a>
      }
    </div>
  )
}

function getMeasOptimizationLog(
  item: NotificationType,
  onLinkClicked: (route: string) => void
): ReactElement | undefined {
  let statusIcon = (
    <StatusIcon
      variant="spinner"
      tooltip={{
        title: i18n.t('Loading...'),
      }}
    />
  )
  let optimizationDescription = ''
  const createdBy = `${i18n.t('Created by')} ${item.creator_name}`
  const status = item.meta?.status
  const startTime = item.meta?.start_time ? Datetime.toLocalTime(item.meta.start_time) : undefined
  const endTime = item.meta?.end_time
    ? Datetime.toLocalTime(Datetime.ISODatetimeTo59Minutes(item.meta.end_time))
    : undefined
  const typeOfOptimization = i18n.t('meas values optimization')
  const period = startTime && endTime ? i18n.t(`{{startTime}} to {{endTime}}`, { startTime, endTime }) : ''
  if (status === 'created' || status === 'running') {
    optimizationDescription = i18n.t('A {{typeOfOptimization}} has been created.', { typeOfOptimization })
  } else if (status === 'queued') {
    statusIcon = (
      <StatusIcon
        variant="queued"
        tooltip={{
          title: i18n.t('Queued'),
        }}
      />
    )
    optimizationDescription = i18n.t('A {{typeOfOptimization}} has been queued.', { typeOfOptimization })
  } else if (status === 'failed' || status === 'unsolvable') {
    statusIcon = (
      <StatusIcon
        variant="followup_failed"
        tooltip={{
          title: failureExplanationKey(status),
        }}
      />
    )
    optimizationDescription = i18n.t('A {{typeOfOptimization}} with the period {{measPeriod}} has failed.', {
      typeOfOptimization,
      measPeriod: period,
    })
  } else if (status === 'finished') {
    statusIcon = (
      <StatusIcon
        variant="followup_finished"
        tooltip={{
          title: i18n.t('Finished'),
        }}
      />
    )
    optimizationDescription = i18n.t('A {{typeOfOptimization}} with the period {{measPeriod}} has been completed.', {
      typeOfOptimization,
      measPeriod: period,
    })
  }

  let link = ''
  if (item?.meta?.start_time && item.meta.end_time) {
    const startTimeDate = item.meta.start_time.split('T')[0]
    const endTimeDate = item.meta.end_time.split('T')[0]
    link = `/followupproduction?startTime=${startTimeDate}&endTime=${endTimeDate}`
  }

  return (
    <div className={styles.NotificationCenter_NotificationItemContainer}>
      <div className={styles.NotificationCenter_NotificationItemContainer_Icon}>{statusIcon}</div>
      <div className={styles.NotificationCenter_NotificationItemContainer_Description}>{optimizationDescription}</div>
      {item.creator_name && (
        <div className={styles.NotificationCenter_NotificationItemContainer_Description__italic}>{createdBy}</div>
      )}
      {
        <a className={styles.NotificationCenter_NotificationItemContainer_Link} onClick={() => onLinkClicked(link)}>
          {i18n.t('Go to the follow-up module')}
        </a>
      }
    </div>
  )
}

function getFuelPlanOptimizationLog(
  item: NotificationType,
  onLinkClicked: (route: string) => void
): ReactElement | undefined {
  let statusIcon = (
    <StatusIcon
      variant="spinner"
      tooltip={{
        title: i18n.t('Loading...'),
      }}
    />
  )
  let optimizationDescription = ''
  const status = item.meta?.status
  const hasSubtype = item.meta?.subtypes
  const subtype = hasSubtype ? hasSubtype.map((subtype: SubtypeObject) => subtype.display_name).join(', ') : ''
  const createdBy = `${i18n.t('Created by')} ${item.creator_name}`
  const typeOfOptimization = i18n.t('fuel plan optimization')
  const period =
    item.meta?.start_time && item.meta?.end_time
      ? `${Datetime.toLocalTime(item.meta?.start_time)} till ${Datetime.toLocalTime(Datetime.ISODatetimeTo59Minutes(item.meta.end_time))}`
      : ''
  if (status === 'created' || status === 'running') {
    if (hasSubtype) {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} for {{subtype}} has been created.', {
        typeOfOptimization: typeOfOptimization,
        subtype: subtype,
      })
    } else {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} has been created.', {
        typeOfOptimization: typeOfOptimization,
      })
    }
  } else if (status === 'queued') {
    statusIcon = (
      <StatusIcon
        variant="queued"
        tooltip={{
          title: i18n.t('Queued'),
        }}
      />
    )
    if (hasSubtype) {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} for {{subtype}} has been queued.', {
        typeOfOptimization: typeOfOptimization,
        subtype: subtype,
      })
    } else {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} has been queued.', {
        typeOfOptimization: typeOfOptimization,
      })
    }
  } else if (status === 'failed' || status === 'unsolvable') {
    statusIcon = (
      <StatusIcon
        variant="fuelplan_failed"
        tooltip={{
          title: failureExplanationKey(status),
        }}
      />
    )
    if (hasSubtype) {
      optimizationDescription = i18n.t(
        'A {{typeOfOptimization}} for {{subtype}} with the period {{fuelPeriod}} has failed.',
        { typeOfOptimization, subtype: subtype, fuelPeriod: period }
      )
    } else {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} with the period {{fuelPeriod}} has failed.', {
        typeOfOptimization: typeOfOptimization,
        fuelPeriod: period,
      })
    }
  } else if (status === 'finished') {
    statusIcon = (
      <StatusIcon
        variant="fuelplan_finished"
        tooltip={{
          title: i18n.t('Finished'),
        }}
      />
    )
    if (hasSubtype) {
      optimizationDescription = i18n.t(
        'A {{typeOfOptimization}} for {{subtype}} with the period {{fuelPeriod}} has been completed.',
        { typeOfOptimization: typeOfOptimization, subtype: subtype, fuelPeriod: period }
      )
    } else {
      optimizationDescription = i18n.t('A {{typeOfOptimization}} with the period {{fuelPeriod}} has been completed.', {
        typeOfOptimization,
        fuelPeriod: period,
      })
    }
  }

  return (
    <div className={styles.NotificationCenter_NotificationItemContainer}>
      <div className={styles.NotificationCenter_NotificationItemContainer_Icon}>{statusIcon}</div>
      <div className={styles.NotificationCenter_NotificationItemContainer_Description}>{optimizationDescription}</div>
      {item.creator_name && (
        <div className={styles.NotificationCenter_NotificationItemContainer_Description__italic}>{createdBy}</div>
      )}

      {isTerminalOptimizationStatus(status) ? (
        <a
          className={styles.NotificationCenter_NotificationItemContainer_Link}
          onClick={() => onLinkClicked('/fuelplan')}
        >
          {i18n.t('Go to scenario planning')}
        </a>
      ) : (
        <></>
      )}
    </div>
  )
}

function getEventLog(
  item: NotificationType,
  openEventModal: (selectedEventPostID: number | undefined) => void = () => {}
): ReactElement | undefined {
  const eventTypeIcons: Record<string, React.JSX.Element> = {
    OperationalEventActivated: (
      <StatusIcon
        variant="event_activated"
        tooltip={{
          title: i18n.t('Activated'),
        }}
      />
    ),
    OperationalEventSent: (
      <StatusIcon
        variant="event_sent"
        tooltip={{
          title: i18n.t('Sent'),
        }}
      />
    ),
    OperationalEventDeactivated: (
      <StatusIcon
        variant="event_deactivated"
        tooltip={{
          title: i18n.t('Deactivated'),
        }}
      />
    ),
    OperationalEventDeleted: (
      <StatusIcon
        variant="event_deleted"
        tooltip={{
          title: i18n.t('Deleted'),
        }}
      />
    ),
  }
  const eventTypeDescription: Record<string, string> = {
    OperationalEventActivated: `Event {{eventTitle}} for timeperiod {{startTime}} - {{endTime}} has been activated.`,
    OperationalEventSent: `Event {{eventTitle}} for timeperiod {{startTime}} - {{endTime}} has been submitted.`,
    OperationalEventDeactivated: `Event {{eventTitle}} for timeperiod {{startTime}} - {{endTime}} has been deactivated.`,
    OperationalEventDeleted: `Event {{eventTitle}} for timeperiod {{startTime}} - {{endTime}} has been deleted.`,
  }
  const eventTypeUpdate: Record<string, string> = {
    OperationalEventActivated: 'Activated by',
    OperationalEventSent: 'Created by',
    OperationalEventDeactivated: 'Deactivated by',
    OperationalEventDeleted: 'Deleted by',
  }
  const operationalEventDescription = i18n.t(eventTypeDescription[item.type] || '', {
    eventTitle: item.meta?.event_name,
    startTime: item.meta?.start_time ? Datetime.toLocalTime(item.meta.start_time) : undefined,
    endTime: item.meta?.end_time
      ? Datetime.toLocalTime(Datetime.ISODatetimeTo59Minutes(item.meta.end_time))
      : undefined,
  })
  const operationalEventID = item.meta?.event_id || undefined
  // created_at is when the event was activated (notification create), not the event creation.
  const activationTime = new Date(item.created_at).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })

  return (
    <div className={styles.NotificationCenter_NotificationItemContainer}>
      <div className={styles.NotificationCenter_NotificationItemContainer_Icon}>{eventTypeIcons[item.type]}</div>
      <div className={styles.NotificationCenter_NotificationItemContainer_Description}>
        {operationalEventDescription}
      </div>
      {item.creator_name && (
        <div className={styles.NotificationCenter_NotificationItemContainer_Description__italic}>
          {i18n.t(eventTypeUpdate[item.type] || 'Updated by')} {item.creator_name}
        </div>
      )}
      {item.type !== 'OperationalEventDeleted' && (
        <a
          className={styles.NotificationCenter_NotificationItemContainer_Link}
          onClick={() => {
            if (operationalEventID) {
              openEventModal(operationalEventID)
            }
          }}
        >
          {i18n.t('Show event')}
        </a>
      )}
      <div className={styles.NotificationCenter_NotificationItemContainer_Time}>{activationTime}</div>
    </div>
  )
}
export function getNotificationItem(
  item: NotificationType,
  onLinkClicked: (route: string) => void,
  openEventModal: (selectedEventPostID: number | undefined) => void = () => {},
  treatRegularAsPublished?: boolean
): ReactElement | undefined {
  const createdBy = `${i18n.t('Created by')} ${item.creator_name}`

  if (item.type === 'admin') {
    return (
      <div className={styles.NotificationCenter_NotificationItemContainer}>
        <div
          className={styles.NotificationCenter_NotificationItemContainer_Description__italic}
        >{`"${item.description}"`}</div>
        <div className={styles.NotificationCenter_NotificationItemContainer_Description}>{createdBy}</div>
      </div>
    )
  } else if (item.type === 'optimization') {
    // If we get regular optimization items, but we use draftproductionplan on this system, treat regular as published optimizations
    if (item.meta?.opt_job_type === 'regular' && treatRegularAsPublished) {
      return getPublishedOptimizationLog(item, onLinkClicked)
    } else if (item.meta?.opt_job_type === 'regular' && !treatRegularAsPublished) {
      // If we get regular optimization items, treat them as regular optimizations
      return getRegularOptimizationLog(item, onLinkClicked)
    }

    if (item.meta?.opt_job_type === 'regular_wip') {
      return getRegularWipOptimizationLog(item, onLinkClicked)
    }
    if (
      item.meta?.opt_job_type === 'followup_with_deviations' ||
      item.meta?.opt_job_type === 'followup_without_deviations'
    ) {
      return getFollowupOptimizationLog(item, onLinkClicked)
    }
    if (item.meta?.opt_job_type === 'sandbox') {
      return getSandboxOptimizationLog(item, onLinkClicked)
    }

    if (item.meta?.opt_job_type === 'measvalues_calculations') {
      return getMeasOptimizationLog(item, onLinkClicked)
    }

    if (item.meta?.opt_job_type === 'fuel_plan') {
      return getFuelPlanOptimizationLog(item, onLinkClicked)
    }
  } else if (item.route === '/events') {
    return getEventLog(item, openEventModal)
  }
}
